/**
 * <autonomy-calculator>
 * Estimates how long a hay/treat pack lasts based on animal type, count and pack weight.
 *
 * Daily consumption assumptions (grams of hay per day, average ranges):
 *  - Rabbit small  (1 – 1.5 kg) : ~ 80 – 120 g
 *  - Rabbit medium (2 – 3 kg)   : ~ 120 – 200 g
 *  - Rabbit large  (4 kg+)      : ~ 200 – 300 g
 *  - Guinea pig                 : ~ 60 – 80 g
 *  - Chinchilla                 : ~ 30 – 50 g
 *
 * Reads pack weight from data-unit-grams. Falls back to parsing kg from
 * the visible variant title in the page if not provided.
 */
class AutonomyCalculator extends HTMLElement {
  constructor() {
    super();

    this.consumption = {
      rabbit: {
        small: { min: 80, max: 120 },
        medium: { min: 120, max: 200 },
        large: { min: 200, max: 300 },
      },
      guinea_pig: { min: 60, max: 80 },
      chinchilla: { min: 30, max: 50 },
    };

    // Le calculateur vit dans une <modal-dialog> que le thème déplace vers <body>
    // (global.js, connectedCallback) : les contrôles sont cherchés dans l'élément
    // puis, à défaut, dans la modale retrouvée par son id (data-modal-id).
    this.animalInputs = this.qAll('[data-animal]');
    this.rabbitSizeWrapper = this.q('[data-rabbit-size-wrapper]');
    this.rabbitSizeSelect = this.q('[data-rabbit-size]');
    this.countInput = this.q('[data-count]');
    this.countDecrement = this.q('[data-count-decrement]');
    this.countIncrement = this.q('[data-count-increment]');
    this.resultEl = this.q('[data-result]');
    this.detailEl = this.q('[data-detail]');
    this.summaryValueEl = this.q('[data-summary-value]');
    this.profileEl = this.q('[data-profile]');
    this.formatRows = Array.from(this.qAll('[data-format-row]'));
    this.packsInput = this.q('[data-packs]');
    this.packsDecrement = this.q('[data-packs-decrement]');
    this.packsIncrement = this.q('[data-packs-increment]');
    this.pageQuantityInput = null;
    this.currentVariantId = this.dataset.variantId || '';

    this.unitGrams = parseInt(this.dataset.unitGrams, 10) || 0;
    this.packQuantity = 1;
    this.sectionId = this.dataset.sectionId || '';

    // Translated strings are injected from Liquid via a JSON <script> tag.
    // French defaults keep the component working if that tag is missing
    // (e.g. a stale cached page rendered before this script shipped).
    this.i18n = this.defaultI18n();
  }

  get modalEl() {
    const id = this.dataset.modalId;
    return id ? document.getElementById(id) : null;
  }

  q(selector) {
    return this.querySelector(selector) || (this.modalEl ? this.modalEl.querySelector(selector) : null);
  }

  qAll(selector) {
    const own = this.querySelectorAll(selector);
    if (own.length || !this.modalEl) return own;
    return this.modalEl.querySelectorAll(selector);
  }

  defaultI18n() {
    return {
      noWeight: 'Poids du paquet non renseigné',
      daysOne: '{count} jour',
      daysOther: '{count} jours',
      daysRange: '{min} – {max} jours',
      detail: 'Pour {count} {animal} ({min}–{max} g/jour) sur {weight} kg{packs}',
      packs: ' ({count} paquets)',
      profile: 'Pour {count} {animal} ({min} à {max} g par jour)',
      profilePacks: ', avec {count} colis',
      animals: {
        rabbit: { one: 'lapin', other: 'lapins' },
        guinea_pig: { one: "cochon d'Inde", other: "cochons d'Inde" },
        chinchilla: { one: 'chinchilla', other: 'chinchillas' },
      },
    };
  }

  readI18n() {
    const script = this.querySelector('[data-autonomy-i18n]');
    if (!script) return;
    try {
      const data = this.decodeEntities(JSON.parse(script.textContent));
      this.i18n = {
        ...this.i18n,
        ...data,
        animals: { ...this.i18n.animals, ...(data.animals || {}) },
      };
    } catch (error) {
      // Malformed JSON: keep the French defaults rather than break the UI.
    }
  }

  // The translation filter HTML-escapes apostrophes (&#39;) ; the strings are
  // written with textContent, so they are decoded once here.
  decodeEntities(value) {
    if (typeof value === 'string') {
      if (!value.includes('&')) return value;
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    }
    if (value && typeof value === 'object') {
      const out = Array.isArray(value) ? [] : {};
      Object.keys(value).forEach((key) => {
        out[key] = this.decodeEntities(value[key]);
      });
      return out;
    }
    return value;
  }

  // Replaces {token} placeholders in a translated template with the given values.
  fill(template, vars) {
    return String(template).replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match
    );
  }

  connectedCallback() {
    this.readI18n();

    this.animalInputs.forEach((input) => {
      input.addEventListener('change', () => this.update());
    });
    this.rabbitSizeSelect?.addEventListener('change', () => this.update());
    this.countInput?.addEventListener('input', () => this.update());

    this.countDecrement?.addEventListener('click', () => this.stepCount(-1));
    this.countIncrement?.addEventListener('click', () => this.stepCount(1));

    this.formatRows.forEach((row) => {
      row.querySelector('[data-choose]')?.addEventListener('click', () => this.chooseFormat(row));
    });

    this.packsInput?.addEventListener('change', () => this.setPacks(parseInt(this.packsInput.value, 10)));
    this.packsDecrement?.addEventListener('click', () => this.setPacks(this.packQuantity - 1));
    this.packsIncrement?.addEventListener('click', () => this.setPacks(this.packQuantity + 1));

    this.observeVariantChanges();
    this.observeQuantityChanges();
    this.update();
  }

  stepCount(delta) {
    if (!this.countInput) return;
    const min = parseInt(this.countInput.min, 10) || 1;
    const max = parseInt(this.countInput.max, 10) || 20;
    const current = parseInt(this.countInput.value, 10) || min;
    const next = Math.min(max, Math.max(min, current + delta));
    if (next === current) return;
    this.countInput.value = String(next);
    this.update();
  }

  // Nombre de paquets choisi dans la modale : reporté dans la quantité de la page
  // (bornes min/max de l'input de la page respectées), qui reste la source de vérité.
  setPacks(value) {
    const page = this.pageQuantityInput;
    const min = parseInt(page?.min || this.packsInput?.min, 10) || 1;
    const maxAttr = parseInt(page?.max || this.packsInput?.max, 10);
    const max = maxAttr > 0 ? maxAttr : 99;
    const next = Math.min(max, Math.max(min, isNaN(value) ? min : value));

    if (page) {
      if (parseInt(page.value, 10) !== next) {
        page.value = String(next);
        page.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.packQuantity = next;
        this.update();
      }
      return;
    }
    this.packQuantity = next;
    this.update();
  }

  observeQuantityChanges() {
    // The quantity-input custom element wraps an <input name="quantity"> that
    // mirrors the cart line quantity. We watch every quantity input on the
    // product info block.
    const inputs = document.querySelectorAll(
      'product-info quantity-input input[name="quantity"], product-info input[name="quantity"]'
    );
    if (!inputs.length) return;
    this.pageQuantityInput = inputs[0];

    const sync = () => {
      const value = parseInt(inputs[0].value, 10);
      this.packQuantity = isNaN(value) || value < 1 ? 1 : value;
      this.update();
    };

    inputs.forEach((input) => {
      input.addEventListener('change', sync);
      input.addEventListener('input', sync);
    });

    // Quantity buttons (+ / -) trigger the change event, but if the theme
    // updates the value programmatically without dispatching, fall back to
    // a MutationObserver on the value attribute.
    const observer = new MutationObserver(sync);
    inputs.forEach((input) => {
      observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    });

    sync();
  }

  observeVariantChanges() {
    // Sense theme uses a pubsub system. The variant-change event fires
    // whenever the user picks a new variant in <variant-selects> /
    // <variant-radios>. We re-read the unit weight from the new variant.
    if (typeof subscribe !== 'function') return;

    this._variantUnsubscribe = subscribe('variant-change', (event) => {
      const data = event?.data || {};
      if (this.sectionId && data.sectionId && data.sectionId !== this.sectionId) return;

      const variant = data.variant;
      if (!variant) return;
      if (variant.id) this.currentVariantId = String(variant.id);

      // Try the variant grams field first (Shopify exposes weight in grams),
      // then fall back to parsing the variant title (e.g. "2 kg").
      let grams = 0;
      if (typeof variant.grams === 'number' && variant.grams > 0) {
        grams = variant.grams;
      } else if (variant.title) {
        grams = this.parseKgFromTitle(variant.title);
      }

      if (grams > 0) {
        this.unitGrams = grams;
      }
      this.update();
    });
  }

  // Sélectionne le format correspondant sur la page (la pastille de poids du
  // sélecteur de variantes) puis ferme la modale. Le changement est pris en
  // charge par <variant-selects>, qui met à jour prix, stock et abonnement.
  chooseFormat(row) {
    const value = row.dataset.optionValue;
    const picker = this.sectionId
      ? document.getElementById(`variant-selects-${this.sectionId}`)
      : document.querySelector('variant-selects');
    if (picker && value) {
      const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (!radio) {
        const select = Array.from(picker.querySelectorAll('select')).find((element) =>
          Array.from(element.options).some((option) => option.value === value)
        );
        if (select && select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    this.currentVariantId = row.dataset.variantId || this.currentVariantId;
    this.update();
    const modal = this.modalEl;
    if (modal && typeof modal.hide === 'function') modal.hide();
  }

  rowGrams(row) {
    const grams = parseInt(row.dataset.grams, 10);
    if (grams > 0) return grams;
    const title = row.querySelector('.product__autonomy-row-title')?.textContent || '';
    return this.parseKgFromTitle(title);
  }

  updateFormats(count, animal, perDay) {
    const forms = this.i18n.animals[animal] || { one: animal, other: animal };
    const animalLabel = count > 1 ? forms.other : forms.one;
    const packs = Math.max(1, this.packQuantity);
    if (this.profileEl) {
      const packsSuffix = packs > 1 ? this.fill(this.i18n.profilePacks, { count: packs }) : '';
      this.profileEl.textContent =
        this.fill(this.i18n.profile, {
          count,
          animal: animalLabel,
          min: perDay.min,
          max: perDay.max,
        }) + packsSuffix;
    }

    const dailyMin = perDay.min * count;
    const dailyMax = perDay.max * count;

    // Chaque ligne = durée de N colis de ce format (N = quantité choisie).
    this.formatRows.forEach((row) => {
      const grams = this.rowGrams(row) * packs;
      const daysEl = row.querySelector('[data-format-days]');
      let range = '—';
      if (grams > 0) {
        range = this.formatRange(Math.floor(grams / dailyMax), Math.floor(grams / dailyMin));
      }
      if (daysEl) daysEl.textContent = range;

      const isCurrent = row.dataset.variantId === this.currentVariantId;
      if (isCurrent) {
        row.setAttribute('aria-current', 'true');
      } else {
        row.removeAttribute('aria-current');
      }
      const tag = row.querySelector('[data-current-tag]');
      if (tag) tag.hidden = !isCurrent;
      const choose = row.querySelector('[data-choose]');
      if (choose) choose.hidden = isCurrent;
    });

    if (this.packsInput && parseInt(this.packsInput.value, 10) !== packs) {
      this.packsInput.value = String(packs);
    }
  }

  disconnectedCallback() {
    if (typeof this._variantUnsubscribe === 'function') {
      this._variantUnsubscribe();
    }
  }

  parseKgFromTitle(title) {
    const match = title.toLowerCase().match(/([0-9]+(?:[.,][0-9]+)?)\s*kg/);
    if (!match) return 0;
    const value = parseFloat(match[1].replace(',', '.'));
    return Math.round(value * 1000);
  }

  getAnimal() {
    const checked = this.q('[data-animal]:checked');
    return checked ? checked.value : 'rabbit';
  }

  update() {
    const animal = this.getAnimal();
    const count = Math.max(1, parseInt(this.countInput?.value, 10) || 1);

    if (this.rabbitSizeWrapper) {
      this.rabbitSizeWrapper.style.display = animal === 'rabbit' ? '' : 'none';
    }

    let perDay;
    if (animal === 'rabbit') {
      const size = this.rabbitSizeSelect?.value || 'medium';
      perDay = this.consumption.rabbit[size];
    } else {
      perDay = this.consumption[animal];
    }

    if (this.formatRows.length) {
      this.updateFormats(count, animal, perDay);
      return;
    }

    if (!this.unitGrams || this.unitGrams <= 0) {
      if (this.resultEl) this.resultEl.textContent = '—';
      if (this.detailEl) this.detailEl.textContent = this.i18n.noWeight;
      if (this.summaryValueEl) this.summaryValueEl.textContent = '—';
      return;
    }

    const totalGrams = this.unitGrams * this.packQuantity;
    const dailyMin = perDay.min * count;
    const dailyMax = perDay.max * count;

    const daysMax = Math.floor(totalGrams / dailyMin);
    const daysMin = Math.floor(totalGrams / dailyMax);

    const range = this.formatRange(daysMin, daysMax);
    if (this.resultEl) this.resultEl.textContent = range;
    if (this.detailEl) this.detailEl.textContent = this.formatDetail(count, animal, perDay, totalGrams);
    if (this.summaryValueEl) this.summaryValueEl.textContent = range;
  }

  formatRange(min, max) {
    if (min === max) {
      const template = min === 1 ? this.i18n.daysOne : this.i18n.daysOther;
      return this.fill(template, { count: min });
    }
    return this.fill(this.i18n.daysRange, { min, max });
  }

  formatDetail(count, animal, perDay, totalGrams) {
    const forms = this.i18n.animals[animal] || { one: animal, other: animal };
    const animalLabel = count > 1 ? forms.other : forms.one;

    const lang = document.documentElement.lang || 'fr';
    const kg = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(totalGrams / 1000);
    const packs = this.packQuantity > 1 ? this.fill(this.i18n.packs, { count: this.packQuantity }) : '';

    return this.fill(this.i18n.detail, {
      count,
      animal: animalLabel,
      min: perDay.min,
      max: perDay.max,
      weight: kg,
      packs,
    });
  }
}

if (!customElements.get('autonomy-calculator')) {
  customElements.define('autonomy-calculator', AutonomyCalculator);
}
